import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import {
  Container, Box, Typography, IconButton, Grid, Paper, Button, Chip,
  Select, MenuItem, Alert, Divider, Stack,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  TextField, Autocomplete,
  ToggleButton, ToggleButtonGroup, FormControl, InputLabel,
  InputAdornment, LinearProgress, CircularProgress, Tooltip, Collapse, alpha,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  useTheme, useMediaQuery
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import RestoreIcon from '@mui/icons-material/Restore';
import SearchIcon from '@mui/icons-material/Search';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import HomeIcon from '@mui/icons-material/Home';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import SavingsIcon from '@mui/icons-material/Savings';
import CategoryIcon from '@mui/icons-material/Category';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { BudgetFrequency, Currency } from '../constants/enums';
import LoadingOverlay from '../components/LoadingOverlay';
import SummaryCard from '../components/dashboard/SummaryCard';
import { useBudgetRates } from '../hooks/useBudgetRates';
import FormDialog, { FormField, FormSection, arborGreen, creamBg, filledInputSx } from '../components/FormDialog';
import AdvisorInsightsSection from '../components/AdvisorInsightsSection';
import QuickCategorySelectDialog from '../components/QuickCategorySelectDialog';
import { useTranslation } from 'react-i18next';
import { sortCategoriesHierarchically } from '../utils/category';
import { BUDGET_MONTHS as MONTHS, formatSpecificMonthsText } from '../utils/budget';
import { handleNumericFocus } from '../utils/inputUtils';

// ─────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────
const CURRENT_YEAR = new Date().getFullYear();



const EMPTY_ITEM = {
  category_id: '',
  parent_budget_item_id: null,
  description: '',
  amount: '',
  frequency: BudgetFrequency.MONTHLY,
  months: [],
  currency: 'USD'
};

const NO_SPIN_SX = {
  '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
    '-webkit-appearance': 'none',
    margin: 0,
  },
  '& input[type=number]': {
    '-moz-appearance': 'textfield',
  },
};

const ICON_RULES = [
  { keywords: ['Housing', 'Rent', 'Mortgage', 'Home'], icon: <HomeIcon /> },
  { keywords: ['Food', 'Groceries', 'Grocery'], icon: <RestaurantIcon /> },
  { keywords: ['Dining', 'Restaurant', 'Eat'], icon: <RestaurantIcon /> },
  { keywords: ['Transport', 'Car', 'Auto', 'Vehicle', 'Gas'], icon: <DirectionsCarIcon /> },
  { keywords: ['Shopping', 'Clothing', 'Clothes'], icon: <ShoppingBagIcon /> },
  { keywords: ['Health', 'Medical', 'Doctor', 'Pharmacy'], icon: <MedicalServicesIcon /> },
  { keywords: ['Entertainment', 'Games', 'Leisure', 'Fun'], icon: <SportsEsportsIcon /> },
  { keywords: ['Utilities', 'Electric', 'Water', 'Internet', 'Phone'], icon: <SavingsIcon /> },
  { keywords: ['Savings', 'Investment'], icon: <SavingsIcon /> },
  { keywords: ['Income', 'Salary', 'Revenue'], icon: <TrendingUpIcon /> },
];

/**
 * Returns the correct currency symbol for a given ISO code.
 */
const getCurrencySymbol = (currency) => {
  const symbols = { USD: '$', CAD: '$', EUR: '€', GBP: '£', CNY: '¥', JPY: '¥', KRW: '₩', INR: '₹', MXN: '$', AUD: '$', HKD: '$', CHF: 'Fr' };
  return symbols[currency] || currency || '$';
};

const formatWithCommas = (val) => {
  if (val === null || val === undefined || val === '') return '';
  const parts = val.toString().split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.join('.');
};

const stripCommas = (val) => val.toString().replace(/,/g, '');

/**
 * Returns "≈ $7,400 USD" when from !== to and rate is known, else null.
 */
const buildConverted = (amount, fromCurrency, toCurrency, rates, formatCurrency) => {
  if (!rates || !fromCurrency || fromCurrency === toCurrency) return null;
  const rate = rates[fromCurrency];
  if (!rate) return null;
  const converted = amount * rate;
  return `≈ ${formatCurrency(converted, toCurrency)} ${toCurrency}`;
};

/**
 * Inline badge showing the ≈ converted amount in the preferred currency.
 */
const ConvertedBadge = ({ text }) => {
  if (!text) return null;
  return (
    <Typography
      component="span"
      variant="caption"
      sx={{ opacity: 0.55, fontWeight: 600, color: 'text.secondary', ml: 0.75, display: 'inline-block', wordBreak: 'break-word' }}
    >
      {text}
    </Typography>
  );
};

// ─────────────────────────────────────────────
// Components
// ─────────────────────────────────────────────

const BudgetTransactionsModal = React.memo(({
  open,
  onClose,
  api,
  ledgerId,
  categoryName,
  categoryIds,
  isUncategorized = false,
  year,
  month,
  formatCurrency,
  preferredCurrency,
  categories = [],
  onTransactionUpdated
}) => {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const [entries, setEntries] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalSum, setTotalSum] = useState(0);

  // Category Quick Edit State
  const [selectedTxForCategory, setSelectedTxForCategory] = useState(null);
  const [isCategorySaving, setIsCategorySaving] = useState(false);
  const [fadingOutIds, setFadingOutIds] = useState(new Set());
  const [fadingInIds, setFadingInIds] = useState(new Set());

  const periodLabel = useMemo(() => {
    if (month === 'ALL') return `${t('Full Year')} ${year}`;
    const mObj = MONTHS.find(m => m.value === month);
    return `${mObj ? t(mObj.label) : ''} ${year}`;
  }, [year, month, t]);

  const fetchTransactions = useCallback(async (isInitial = true) => {
    if (!open || !ledgerId) return;
    if (isInitial) setIsLoading(true);
    try {
      let startDate = `${year}-01-01T00:00:00`;
      let endDate = `${year}-12-31T23:59:59`;
      if (month !== 'ALL') {
        const mStr = String(month).padStart(2, '0');
        const lastDay = new Date(year, month, 0).getDate();
        const lStr = String(lastDay).padStart(2, '0');
        startDate = `${year}-${mStr}-01T00:00:00`;
        endDate = `${year}-${mStr}-${lStr}T23:59:59`;
      }

      const params = new URLSearchParams();
      params.append('start_date', startDate);
      params.append('end_date', endDate);
      params.append('include_exchanged_amount', 'true');
      params.append('limit', '300');

      if (isUncategorized) {
        params.append('uncategorized_only', 'true');
      } else if (categoryIds && categoryIds.length > 0) {
        categoryIds.forEach(id => {
          if (id) params.append('category_ids', id);
        });
      }

      const res = await api.get(`/ledger/ledgers/${ledgerId}/entries?${params.toString()}`);
      const rawItems = res.data?.items || (Array.isArray(res.data) ? res.data : []);
      // Spending entries: for categorized items include credits (reimbursements net against spending);
      // for uncategorized items only include debits (> 0).
      const spendingItems = rawItems.filter(e => {
        if (isUncategorized) {
          return parseFloat(e.amount || 0) > 0;
        }
        return true;
      });

      let sum = 0;
      spendingItems.forEach(e => {
        const amt = parseFloat(e.exchanged_amount !== undefined && e.exchanged_amount !== null ? e.exchanged_amount : e.amount || 0);
        sum += amt;
      });

      if (isInitial) {
        setEntries(spendingItems);
        setTotalSum(sum);
      } else {
        // Diffing on reload
        setEntries(prevEntries => {
          const oldIds = new Set(prevEntries.map(e => e.id));
          const newIds = new Set(spendingItems.map(e => e.id));

          const removedIds = prevEntries.filter(e => !newIds.has(e.id)).map(e => e.id);
          const addedIds = spendingItems.filter(e => !oldIds.has(e.id)).map(e => e.id);

          if (removedIds.length > 0) {
            setFadingOutIds(new Set(removedIds));
            // Keep removed entries in state during the 350ms fade-out transition
            setTimeout(() => {
              setEntries(spendingItems);
              setFadingOutIds(new Set());
            }, 350);

            return prevEntries.map(e => {
              const updated = spendingItems.find(n => n.id === e.id);
              return updated || e;
            });
          }

          if (addedIds.length > 0) {
            setFadingInIds(new Set(addedIds));
            setTimeout(() => {
              setFadingInIds(new Set());
            }, 400);
          }

          return spendingItems;
        });
        setTotalSum(sum);
      }
    } catch (err) {
      console.error('Failed to fetch budget transactions:', err);
    } finally {
      if (isInitial) setIsLoading(false);
    }
  }, [open, ledgerId, year, month, categoryIds, isUncategorized, api]);

  useEffect(() => {
    fetchTransactions(true);
  }, [fetchTransactions]);

  const handleSelectCategory = async (category) => {
    if (!selectedTxForCategory) return;
    const entry = selectedTxForCategory;
    setIsCategorySaving(true);
    try {
      const newCatId = category?.id ? Number(category.id) : null;
      const payload = {
        description: entry.description,
        amount: entry.amount,
        currency: entry.currency || 'USD',
        date: entry.date,
        category_id: newCatId,
        recurring_transaction_id: entry.recurring_transaction_id || null,
        credit_card_id: entry.credit_card_id || null,
        source_document_id: entry.source_document_id || null,
        cash_back_amount: entry.cash_back_amount || null,
      };

      await api.put(`/ledger/${entry.id}`, payload);
      setSelectedTxForCategory(null);
      showToast(t('Category updated successfully'), 'success');

      // Reload server data and diff with animations
      await fetchTransactions(false);

      // Notify parent page to sync
      if (onTransactionUpdated) {
        onTransactionUpdated();
      }
    } catch (err) {
      console.error('Failed to update category:', err);
      showToast(err.response?.data?.detail || t('Failed to update category'), 'error');
    } finally {
      setIsCategorySaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: creamBg,
            borderRadius: { xs: 0, sm: '24px' },
            backgroundImage: 'none',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ p: 3, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <ReceiptLongIcon sx={{ color: arborGreen, fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.primary', lineHeight: 1.2 }}>
                {isUncategorized
                  ? t('Uncategorized Spending Transactions')
                  : (categoryName ? `${t(categoryName)} ${t('Transactions')}` : t('Budget Spending Transactions'))}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {periodLabel} · {entries.filter(e => !fadingOutIds.has(e.id)).length} {entries.filter(e => !fadingOutIds.has(e.id)).length === 1 ? t('transaction') : t('transactions')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ textAlign: 'right' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700, display: 'block' }}>
              {t('TOTAL SPENT')}
            </Typography>
            <Typography variant="subtitle1" sx={{ fontWeight: 800, color: arborGreen }}>
              {formatCurrency(totalSum, preferredCurrency)} {preferredCurrency}
            </Typography>
          </Box>
        </Box>

        <DialogContent sx={{ p: 0, maxHeight: 480 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 8 }}>
              <CircularProgress size={36} sx={{ color: arborGreen }} />
            </Box>
          ) : entries.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                {t('No transactions recorded for this period.')}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{t('Date')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{t('Description')}</TableCell>
                    <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{t('Category')}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>{t('Amount')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {entries.map((tx) => {
                    const rawAmt = parseFloat(tx.amount || 0);
                    const isExchanged = tx.exchanged_amount && (tx.currency || 'USD') !== tx.exchanged_currency;
                    const isFadingOut = fadingOutIds.has(tx.id);
                    const isFadingIn = fadingInIds.has(tx.id);

                    return (
                      <TableRow
                        key={tx.id}
                        hover={!isFadingOut}
                        sx={{
                          transition: 'all 0.35s ease-in-out',
                          opacity: isFadingOut ? 0 : 1,
                          transform: isFadingOut ? 'scaleY(0.6)' : 'scaleY(1)',
                          bgcolor: isFadingOut ? alpha('#d32f2f', 0.08) : (isFadingIn ? alpha('#1E4D2B', 0.08) : 'inherit'),
                          pointerEvents: isFadingOut ? 'none' : 'auto',
                        }}
                      >
                        <TableCell sx={{ color: 'text.secondary', fontWeight: 500, whiteSpace: 'nowrap' }}>
                          {new Date(tx.date).toLocaleDateString(i18n.language || undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{tx.description}</TableCell>
                        <TableCell>
                          <Tooltip title={t('Click to change category')} arrow placement="top">
                            <Chip
                              size="small"
                              icon={<EditIcon sx={{ fontSize: '0.8rem !important', opacity: 0.75 }} />}
                              label={tx.category ? t(tx.category.name) : t('Uncategorized')}
                              onClick={() => setSelectedTxForCategory(tx)}
                              variant={tx.category ? 'outlined' : 'filled'}
                              sx={{
                                fontWeight: 600,
                                cursor: 'pointer',
                                height: 24,
                                fontSize: '0.72rem',
                                color: tx.category?.color || (tx.category ? 'primary.main' : 'text.secondary'),
                                borderColor: tx.category?.color || 'divider',
                                bgcolor: tx.category ? alpha(tx.category?.color || '#1E4D2B', 0.08) : alpha('#000', 0.06),
                                fontStyle: tx.category ? 'normal' : 'italic',
                                '&:hover': {
                                  bgcolor: tx.category ? alpha(tx.category?.color || '#1E4D2B', 0.18) : alpha('#000', 0.12),
                                  borderColor: tx.category?.color || 'primary.main',
                                  transform: 'translateY(-1px)',
                                  boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
                                },
                                transition: 'all 0.15s ease'
                              }}
                            />
                          </Tooltip>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {formatCurrency(rawAmt, tx.currency || 'USD')} {tx.currency || 'USD'}
                          </Typography>
                          {isExchanged && (
                            <Typography variant="caption" sx={{ color: 'text.secondary', opacity: 0.7, display: 'block', fontWeight: 600 }}>
                              ≈ {formatCurrency(tx.exchanged_amount, tx.exchanged_currency)} {tx.exchanged_currency}
                            </Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2, px: 3, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#fff' }}>
          <Button onClick={onClose} variant="outlined" sx={{ fontWeight: 700, borderRadius: 2 }}>
            {t('Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Quick Category Select Dialog ── */}
      <QuickCategorySelectDialog
        open={Boolean(selectedTxForCategory)}
        onClose={() => setSelectedTxForCategory(null)}
        currentCategoryId={selectedTxForCategory?.category_id || selectedTxForCategory?.category?.id || null}
        categories={categories}
        onSelectCategory={handleSelectCategory}
        isSaving={isCategorySaving}
      />
    </>
  );
});

const BudgetProgressBar = React.memo(({ spent: rawSpent, income: rawIncome, currency, preferredCurrency, formatCurrency, rates, onProgressClick, isMonthly = false, monthLabel = '' }) => {
  const { t } = useTranslation();
  const spent = parseFloat(rawSpent || 0);
  const income = parseFloat(rawIncome || 0);
  const percent = income > 0 ? Math.min((spent / income) * 100, 100) : 0;
  const isOver = spent > income;
  const remaining = Math.max(0, income - spent);
  const spentConverted = buildConverted(spent, currency, preferredCurrency, rates, formatCurrency);
  const incomeConverted = buildConverted(income, currency, preferredCurrency, rates, formatCurrency);
  const remainingConverted = buildConverted(remaining, currency, preferredCurrency, rates, formatCurrency);

  const spendingHeader = isMonthly ? `${t(monthLabel)} ${t('SPENDING')}` : t('BUDGETED SPENDING');
  const incomeHeader = isMonthly ? `${t(monthLabel)} ${t('INCOME')}` : t('ANNUAL INCOME');
  const tooltipText = isMonthly ? `${t('Total spending budgeted for')} ${t(monthLabel)}.` : t('Total annual spending budgeted across all categories.');

  return (
    <Box sx={{ width: '100%', mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'baseline', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {spendingHeader}
            <Tooltip title={tooltipText}>
              <InfoOutlinedIcon sx={{ fontSize: 14, cursor: 'help' }} />
            </Tooltip>
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: isOver ? 'error.main' : 'text.primary' }}>
              {formatCurrency(spent, currency)}
            </Typography>
            <Typography component="span" variant="caption" sx={{ opacity: 0.6, fontWeight: 600 }}>
              {currency}
            </Typography>
            <ConvertedBadge text={spentConverted} />
          </Box>
        </Box>
        <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, minWidth: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
            {incomeHeader}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
            <Typography variant="h5" sx={{ fontWeight: 800, color: 'success.main' }}>
              {formatCurrency(income, currency)}
            </Typography>
            <Typography component="span" variant="caption" sx={{ opacity: 0.6, fontWeight: 600 }}>
              {currency}
            </Typography>
            <ConvertedBadge text={incomeConverted} />
          </Box>
        </Box>
      </Box>

      <Tooltip title={onProgressClick ? t("Click to view all spending transactions for this period") : ""}>
        <Box
          onClick={onProgressClick}
          sx={{
            position: 'relative',
            height: 12,
            borderRadius: 1,
            bgcolor: 'action.hover',
            overflow: 'hidden',
            mb: 1,
            cursor: onProgressClick ? 'pointer' : 'default',
            transition: 'all 0.2s ease',
            '&:hover': onProgressClick ? {
              boxShadow: '0 0 8px rgba(0,0,0,0.15)',
              transform: 'scaleY(1.15)',
            } : {}
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              top: 0, left: 0, bottom: 0,
              width: `${percent}%`,
              bgcolor: isOver ? '#c62828' : 'primary.main',
              boxShadow: isOver ? '0 0 12px rgba(198, 40, 40, 0.4)' : 'none',
              transition: 'width 0.6s cubic-bezier(0.4, 0, 0.2, 1), background-color 0.3s',
              borderRadius: 1
            }}
          />
        </Box>
      </Tooltip>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 0.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700, color: isOver ? 'error.main' : 'text.secondary' }}>
          {percent.toFixed(1)}% {t('OF INCOME')}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 700, color: isOver ? 'error.main' : 'success.main', wordBreak: 'break-word', textAlign: { xs: 'left', sm: 'right' } }}>
          {isOver ? t('OVER BUDGET') : `${formatCurrency(remaining, currency)} ${currency} ${t('REMAINING SAVINGS')}`}{!isOver && remainingConverted ? ` · ${remainingConverted}` : ''}
        </Typography>
      </Box>
    </Box>
  );
});

// ─────────────────────────────────────────────
// Main Page
// ─────────────────────────────────────────────
export default function Budget() {
  const { t } = useTranslation();
  const { id: ledgerId } = useParams();
  const navigate = useNavigate();
  const { api, formatCurrency, preferredCurrency } = useAuth();
  const { showToast } = useToast();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── State ──
  const [selectedYear, setSelectedYear] = useState(CURRENT_YEAR);
  const [selectedMonth, setSelectedMonth] = useState('ALL');
  const [mode, setMode] = useState('view'); // 'view' | 'edit'
  const [userRole, setUserRole] = useState('viewer');
  const [budget, setBudget] = useState(null);
  const [summary, setSummary] = useState(null);
  const [actuals, setActuals] = useState([]);
  const [uncategorizedActuals, setUncategorizedActuals] = useState([]);
  const [categories, setCategories] = useState([]);
  const [lastYearIncome, setLastYearIncome] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedCats, setExpandedCats] = useState({});
  const expandedCatsInitialized = React.useRef(false);
  const [allYears, setAllYears] = useState([]);

  // ── Income input local state (prevents accidental $0 save on empty blur) ──
  const [incomeInput, setIncomeInput] = useState('');
  const [isIncomeSaving, setIsIncomeSaving] = useState(false);

  // ── Available Years logic ──
  const availableYears = useMemo(() => {
    const years = new Set([CURRENT_YEAR, CURRENT_YEAR + 1]);
    allYears.forEach(y => years.add(y));
    return Array.from(years).sort((a, b) => a - b);
  }, [allYears]);

  const priorYearWithData = useMemo(() => {
    // Find highest year < selectedYear that has data in allYears
    return allYears
      .filter(y => y < selectedYear)
      .sort((a, b) => b - a)[0] || null;
  }, [allYears, selectedYear]);

  // ── Uncategorized Spendings summary for active period ──
  const { uncategorizedCount, uncategorizedAmount } = useMemo(() => {
    if (selectedMonth === 'ALL') {
      const count = uncategorizedActuals.reduce((sum, u) => sum + (u.count || 0), 0);
      const amount = uncategorizedActuals.reduce((sum, u) => sum + parseFloat(u.amount || 0), 0);
      return { uncategorizedCount: count, uncategorizedAmount: amount };
    }
    const monthData = uncategorizedActuals.find(u => u.month === selectedMonth);
    return {
      uncategorizedCount: monthData?.count || 0,
      uncategorizedAmount: parseFloat(monthData?.amount || 0),
    };
  }, [uncategorizedActuals, selectedMonth]);

  const selectedPeriodLabel = useMemo(() => {
    if (selectedMonth === 'ALL') return `${t('Full Year')} ${selectedYear}`;
    const mObj = MONTHS.find(m => m.value === selectedMonth);
    return `${mObj ? t(mObj.label) : ''} ${selectedYear}`;
  }, [selectedYear, selectedMonth, t]);

  // ── Exchange Rates ──
  const uniqueCurrencies = useMemo(() => {
    const seen = new Set();
    if (budget?.currency) seen.add(budget.currency);
    budget?.items?.forEach(cat =>
      cat.sub_items?.forEach(item => { if (item.currency) seen.add(item.currency); })
    );
    return [...seen];
  }, [budget]);

  const { rates } = useBudgetRates({
    ledgerId,
    currencies: uniqueCurrencies,
    toCurrency: preferredCurrency,
  });

  // Rates for converting sub-items to the budget's base currency (used in category totals)
  const budgetCurrencyToUse = budget?.currency || preferredCurrency;
  const { rates: budgetRatesFromHook } = useBudgetRates({
    ledgerId,
    currencies: uniqueCurrencies,
    toCurrency: budgetCurrencyToUse,
  });

  const budgetRates = useMemo(() => {
    if (!budget?.currency || budget.currency === preferredCurrency) {
      return rates;
    }
    return budgetRatesFromHook;
  }, [budget?.currency, preferredCurrency, rates, budgetRatesFromHook]);

  // ── Memoized actuals lookup map: categoryId -> { ALL: total, [month]: total } ──
  const actualsByCategoryMap = useMemo(() => {
    const map = new Map();
    for (const a of actuals) {
      const amt = parseFloat(a.amount || 0);
      if (!map.has(a.category_id)) {
        map.set(a.category_id, { ALL: 0 });
      }
      const catObj = map.get(a.category_id);
      catObj.ALL = (catObj.ALL || 0) + amt;
      catObj[a.month] = (catObj[a.month] || 0) + amt;
    }
    return map;
  }, [actuals]);

  // ── Dialog State ──
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [currentItem, setCurrentItem] = useState(null);
  const [formData, setFormData] = useState(EMPTY_ITEM);
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneOverwrite, setCloneOverwrite] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [addingCategoryId, setAddingCategoryId] = useState(null);
  const [parentItemContext, setParentItemContext] = useState(null);
  // ── Delete Confirmation Dialog ──
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // ── Transactions Modal State ──
  const [txModalState, setTxModalState] = useState({
    open: false,
    categoryName: '',
    categoryIds: [],
    isUncategorized: false,
  });

  const handleOpenTxModal = useCallback((name, categoryIds, isUncategorized = false) => {
    setTxModalState({
      open: true,
      categoryName: name,
      categoryIds: categoryIds || [],
      isUncategorized: Boolean(isUncategorized),
    });
  }, []);

  const handleCloseTxModal = useCallback(() => {
    setTxModalState(prev => ({ ...prev, open: false }));
  }, []);

  // ── Memoized unbudgeted categories for category selection modal ──
  const availableCategories = useMemo(() => {
    const existingCatIds = new Set(budget?.items?.map(item => item.category_id) || []);
    const searchLower = (categorySearch || '').toLowerCase();
    const filtered = categories.filter(cat =>
      (!cat.category_type || cat.category_type === 'debit') &&
      !existingCatIds.has(cat.id) &&
      ((t(cat.name) || cat.name || '').toLowerCase().includes(searchLower))
    );
    return sortCategoriesHierarchically(filtered, t);
  }, [budget?.items, categories, categorySearch, t]);

  const sortedBudgetItems = useMemo(() => {
    if (!budget?.items) return [];
    return [...budget.items].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  }, [budget?.items]);

  // ─────────────────────────────────────────────
  // Data fetching
  // ─────────────────────────────────────────────
  const fetchAllData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Parallelize all calls to eliminate waterfall round-trips
      const [budgetRes, summaryRes, catRes, actualsRes, ledgerRes] = await Promise.all([
        api.get(`/ledger/ledgers/${ledgerId}/budget/${selectedYear}`),
        api.get(`/ledger/ledgers/${ledgerId}/budget/${selectedYear}/summary`).catch(() => ({ data: null })),
        api.get(`/categories/ledger/${ledgerId}`).catch(() => ({ data: [] })),
        api.get(`/ledger/ledgers/${ledgerId}/budget/${selectedYear}/actuals`).catch(() => ({ data: { actuals: [], uncategorized: [] } })),
        api.get('/ledger/ledgers').catch(() => ({ data: [] }))
      ]);

      if (ledgerRes?.data) {
        const currentLedger = ledgerRes.data.find(l => l.id.toString() === ledgerId.toString());
        if (currentLedger) {
          const role = currentLedger.role || 'viewer';
          setUserRole(role);
          if (role === 'viewer') {
            setMode('view');
          }
        }
      }

      if (budgetRes.data) {
        setBudget(budgetRes.data);
        setLastYearIncome(budgetRes.data.suggested_income || 0);
        setIncomeInput(budgetRes.data.income_amount?.toString() || '0');
      }

      if (summaryRes.data) setSummary(summaryRes.data);
      if (catRes.data) setCategories(catRes.data);
      setActuals(actualsRes.data?.actuals || []);
      setUncategorizedActuals(actualsRes.data?.uncategorized || []);

      // Only expand all on first load; preserve user's state on subsequent fetches
      if (!expandedCatsInitialized.current && budgetRes.data?.items) {
        const initialExpanded = {};
        budgetRes.data.items.forEach(item => {
          if (item.category_id) initialExpanded[item.category_id] = true;
        });
        setExpandedCats(initialExpanded);
        expandedCatsInitialized.current = true;
      }

    } catch (err) {
      console.error('Failed to fetch budget data:', {
        message: err.message,
        status: err.response?.status,
        url: err.config?.url,
        data: err.response?.data
      });
      // Don't show toast for 404s as they are handled by the UI (empty state)
      if (err.response?.status !== 404) {
        showToast(`Failed to load budget data: ${err.response?.data?.detail || err.message}`, 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [api, ledgerId, selectedYear, showToast]);

  const fetchYears = useCallback(async () => {
    try {
      const res = await api.get(`/ledger/ledgers/${ledgerId}/budget/years`);
      setAllYears(res.data);
    } catch (err) {
      console.error('Failed to fetch budget years', err);
    }
  }, [api, ledgerId]);

  useEffect(() => {
    fetchAllData();
    fetchYears();
  }, [fetchAllData, fetchYears]);

  // Reset expanded state when year changes so new year opens fully expanded
  useEffect(() => {
    expandedCatsInitialized.current = false;
    setExpandedCats({});
  }, [selectedYear]);


  // ─────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────
  const toggleExpand = (catId) => {
    setExpandedCats(prev => {
      const isCurrentlyExpanded = prev[catId] !== false;
      return { ...prev, [catId]: !isCurrentlyExpanded };
    });
  };

  const handleUpdateBudget = async (updates) => {
    setIsIncomeSaving(true);
    try {
      await api.put(`/ledger/ledgers/${ledgerId}/budget/${selectedYear}`, updates);
      fetchAllData();
      showToast('Budget updated.', 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to update budget.', 'error');
    } finally {
      setIsIncomeSaving(false);
    }
  };

  const handleIncomeBlur = () => {
    const raw = stripCommas(incomeInput);
    const parsed = parseFloat(raw);
    // If input is empty or invalid, restore to the last saved value without saving $0
    if (incomeInput === '' || isNaN(parsed)) {
      setIncomeInput(budget?.income_amount?.toString() || '0');
      return;
    }
    handleUpdateBudget({ income_amount: parsed });
  };


  const handleOpenAdd = (parentItem = null) => {
    setCurrentItem(null);
    setParentItemContext(parentItem);
    setFormData({
      ...EMPTY_ITEM,
      category_id: parentItem?.category_id || '',
      parent_budget_item_id: parentItem?.id || null,
      currency: budget?.currency || preferredCurrency || 'USD'
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (item) => {
    setCurrentItem(item);
    setParentItemContext(null);
    setFormData({
      category_id: item.category_id,
      parent_budget_item_id: item.parent_budget_item_id,
      description: item.name,
      amount: item.amount.toString(),
      frequency: item.frequency_type,
      months: item.months || [],
      currency: item.currency || budget?.currency || 'USD'
    });
    setDialogOpen(true);
  };

  const handleAddCategory = async (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    if (!cat) return;

    setAddingCategoryId(categoryId);
    setIsRefreshing(true);
    try {
      const payload = {
        category_id: categoryId,
        name: cat.name,
        amount: 0,
        frequency_type: 'MONTHLY',
        currency: budget?.currency || preferredCurrency || 'USD'
      };
      await api.post(`/ledger/ledgers/${ledgerId}/budget/${selectedYear}/items`, payload);
      setExpandedCats(prev => ({ ...prev, [categoryId]: true }));
      setCategoryDialogOpen(false);
      showToast(`${cat.name} added to budget.`, 'success');
      await fetchAllData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to add category.', 'error');
    } finally {
      setAddingCategoryId(null);
      setIsRefreshing(false);
    }
  };

  const handleSaveItem = async () => {
    if (!formData.category_id) return showToast('Please select a category.', 'error');
    if (!formData.amount || isNaN(parseFloat(formData.amount))) return showToast('Please enter a valid amount.', 'error');

    if (formData.frequency === BudgetFrequency.SPECIFIC_MONTHS && (!formData.months || formData.months.length === 0)) {
      return showToast(t('Please select at least one month.'), 'error');
    }

    let payloadFrequency = formData.frequency;
    let payloadMonths = payloadFrequency === BudgetFrequency.SPECIFIC_MONTHS ? formData.months : null;

    if (payloadFrequency === BudgetFrequency.SPECIFIC_MONTHS && payloadMonths?.length === 12) {
      payloadFrequency = BudgetFrequency.MONTHLY;
      payloadMonths = null;
    }

    const amt = parseFloat(formData.amount);
    const newContribution = payloadFrequency === BudgetFrequency.MONTHLY
      ? amt * 12
      : amt * (payloadMonths?.length || 0);

    // Client-side spend ceiling validation (mirrors backend _validate_spend_ceiling)
    if (budget?.income_amount > 0) {
      const existingTotal = (summary?.total_budgeted || 0);
      // If editing, subtract the current item's contribution first
      let currentContribution = 0;
      if (currentItem) {
        currentContribution = currentItem.frequency_type === BudgetFrequency.MONTHLY
          ? parseFloat(currentItem.amount) * 12
          : parseFloat(currentItem.amount) * (currentItem.months?.length || 0);
      }
      const projected = existingTotal - currentContribution + newContribution;
      if (projected > parseFloat(budget.income_amount)) {
        return showToast(
          `This item would push total spending to ${formatCurrency(projected, budget.currency)}, exceeding your annual income of ${formatCurrency(budget.income_amount, budget.currency)}.`,
          'error'
        );
      }
    }

    const payload = {
      category_id: formData.category_id,
      parent_budget_item_id: formData.parent_budget_item_id,
      name: formData.description,
      amount: amt,
      frequency_type: payloadFrequency,
      months: payloadMonths,
      currency: formData.currency
    };

    setIsSaving(true);
    setIsRefreshing(true);
    try {
      if (currentItem) {
        await api.put(`/ledger/ledgers/${ledgerId}/budget/${selectedYear}/items/${currentItem.id}`, payload);
        showToast('Budget item updated.', 'success');
      } else {
        await api.post(`/ledger/ledgers/${ledgerId}/budget/${selectedYear}/items`, payload);
        showToast('Budget item added.', 'success');
      }

      if (formData.parent_budget_item_id) {
        setExpandedCats(prev => ({
          ...prev,
          [formData.parent_budget_item_id]: true,
          [formData.category_id]: true,
          ...(parentItemContext?.id ? { [parentItemContext.id]: true } : {})
        }));
      }
      setDialogOpen(false);
      await fetchAllData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to save item.', 'error');
    } finally {
      setIsSaving(false);
      setIsRefreshing(false);
    }
  };

  const handleDeleteItem = (itemId) => {
    setItemToDelete(itemId);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    const deletedId = itemToDelete;
    setDeleteConfirmOpen(false);
    setItemToDelete(null);
    setIsRefreshing(true);
    try {
      await api.delete(`/ledger/ledgers/${ledgerId}/budget/${selectedYear}/items/${deletedId}`);
      showToast('Item removed.', 'success');
      await fetchAllData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to delete item.', 'error');
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSeedTemplate = async () => {
    try {
      setIsLoading(true);
      await api.post(`/ledger/ledgers/${ledgerId}/budget/${selectedYear}/seed`);
      await fetchAllData();
      fetchYears();
      showToast('Budget initialized from historical categories.', 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to initialize budget.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloneBudget = async () => {
    if (!priorYearWithData) return;
    setIsSaving(true);
    try {
      setIsLoading(true);
      await api.post(`/ledger/ledgers/${ledgerId}/budget/${selectedYear}/clone`, null, {
        params: {
          from_year: priorYearWithData,
          overwrite: cloneOverwrite
        }
      });
      showToast(`Budget cloned from ${priorYearWithData}.`, 'success');
      setCloneDialogOpen(false);
      fetchAllData();
      fetchYears();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Failed to clone budget.', 'error');
    } finally {
      setIsLoading(false);
      setIsSaving(false);
    }
  };

  // ─────────────────────────────────────────────
  // Render Helpers
  // ─────────────────────────────────────────────
  const getCategoryName = (id) => {
    const cat = categories.find(c => c.id === id);
    return cat ? cat.name : 'Unknown Category';
  };

  // ─────────────────────────────────────────────
  // Render
  // ─────────────────────────────────────────────
  return (
    <Container maxWidth="lg" sx={{ mt: { xs: 3, sm: 6 }, mb: { xs: 6, sm: 12 }, px: { xs: 1.5, sm: 3 }, width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
      <LoadingOverlay open={isLoading && !budget} />

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 2, mb: { xs: 4, sm: 6 }, width: '100%' }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 1, fontSize: { xs: '1.75rem', sm: '2.125rem' } }}>
            {t('Annual Budget')}
          </Typography>
        </Box>

        {userRole !== 'viewer' && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
            {priorYearWithData && (
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                onClick={() => {
                  setCloneOverwrite(false);
                  setCloneDialogOpen(true);
                }}
                sx={{ fontWeight: 700 }}
              >
                {t('Clone from')} {priorYearWithData}
              </Button>
            )}

            {mode === 'edit' && budget?.has_prior_data && budget.items.length === 0 && (
              <Button
                variant="outlined"
                startIcon={<RestoreIcon />}
                onClick={handleSeedTemplate}
                sx={{ fontWeight: 700 }}
              >
                {t('Initialize from History')}
              </Button>
            )}

            <Button
              variant={mode === 'view' ? "contained" : "outlined"}
              color="primary"
              startIcon={mode === 'view' ? <EditIcon /> : <VisibilityIcon />}
              onClick={() => setMode(mode === 'view' ? 'edit' : 'view')}
              sx={{ fontWeight: 700, px: 3 }}
            >
              {mode === 'view' ? t('Edit Budget') : t('View Budget')}
            </Button>
          </Box>
        )}
      </Box>

      {/* ── Month & Year Filter ── */}
      {isMobile ? (
        <Box sx={{ mb: 3, display: 'flex', gap: 1.5, width: '100%' }}>
          <FormControl size="small" sx={{ flex: '1 1 35%', minWidth: 90 }}>
            <InputLabel id="mobile-year-select-label">{t('Year')}</InputLabel>
            <Select
              labelId="mobile-year-select-label"
              id="mobile-year-select"
              value={selectedYear}
              label={t('Year')}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ bgcolor: 'background.paper' }}
            >
              {availableYears.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ flex: '2 1 65%', minWidth: 140 }}>
            <InputLabel id="mobile-month-select-label">{t('Period')}</InputLabel>
            <Select
              labelId="mobile-month-select-label"
              id="mobile-month-select"
              value={selectedMonth}
              label={t('Period')}
              onChange={(e) => setSelectedMonth(e.target.value)}
              sx={{ bgcolor: 'background.paper' }}
            >
              <MenuItem value="ALL">{t('All Months (Full Year)')}</MenuItem>
              <Divider sx={{ my: 0.5 }} />
              {MONTHS.map(m => (
                <MenuItem key={m.value} value={m.value}>
                  {t(m.label)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      ) : (
        <Box sx={{ mb: 4, width: '100%', maxWidth: '100%', minWidth: 0, display: 'flex', alignItems: 'center', gap: 1.5, overflowX: 'auto', WebkitOverflowScrolling: 'touch', pb: 1, '&::-webkit-scrollbar': { height: 4 } }}>
          <FormControl size="small" sx={{ minWidth: 90, flexShrink: 0 }}>
            <InputLabel id="year-select-label">{t('Year')}</InputLabel>
            <Select
              labelId="year-select-label"
              id="year-select"
              value={selectedYear}
              label={t('Year')}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ bgcolor: 'background.paper' }}
            >
              {availableYears.map(y => (
                <MenuItem key={y} value={y}>{y}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1} flexWrap="nowrap" useFlexGap sx={{ gap: 0.75, minWidth: 'max-content', flexShrink: 0 }}>
            <Button
              variant={selectedMonth === 'ALL' ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setSelectedMonth('ALL')}
              sx={{ fontWeight: 600, borderRadius: 3, minWidth: 40, px: 1.5, py: 0.5 }}
            >
              {t('All')}
            </Button>
            {MONTHS.map(m => (
              <Button
                key={m.value}
                variant={selectedMonth === m.value ? 'contained' : 'outlined'}
                size="small"
                onClick={() => setSelectedMonth(m.value)}
                sx={{ fontWeight: 600, borderRadius: 3, minWidth: 40, px: 1.5, py: 0.5 }}
              >
                {t(m.short)}
              </Button>
            ))}
          </Stack>
        </Box>
      )}

      {summary && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 2fr' }, gap: 3, mb: 4, alignItems: 'stretch' }}>
          <Box sx={{ minWidth: 0, height: '100%' }}>
            {(() => {
              const currency = budget?.currency || preferredCurrency;
              const isAll = selectedMonth === 'ALL';
              const periodBudget = isAll
                ? (summary.total_budgeted || 0)
                : (summary.monthly?.find(m => m.month === selectedMonth)?.total || 0);

              const periodActualSpent = actuals.reduce((sum, a) => {
                if (isAll || a.month === selectedMonth) {
                  return sum + parseFloat(a.amount || 0);
                }
                return sum;
              }, 0);

              const spentPercent = periodBudget > 0
                ? (periodActualSpent / periodBudget) * 100
                : (periodActualSpent > 0 ? 100.1 : 0);
              const convertedSpent = buildConverted(periodActualSpent, currency, preferredCurrency, rates, formatCurrency);
              const budgetDiff = periodBudget - periodActualSpent;
              const isOverBudget = budgetDiff < 0;
              const absBudgetDiff = Math.abs(budgetDiff);
              const diffLabel = isOverBudget ? t('over') : t('remaining');
              const convertedDiff = buildConverted(absBudgetDiff, currency, preferredCurrency, rates, formatCurrency);

              // Determine color thresholding:
              // on budget (< 80%): green ('dark')
              // close to over budget (80% - 100%): yellow/amber ('warning')
              // over budget (> 100%): red ('error')
              let cardVariant = 'dark';
              if (spentPercent > 100) {
                cardVariant = 'error';
              } else if (spentPercent >= 80) {
                cardVariant = 'warning';
              }

              return (
                <SummaryCard
                  variant={cardVariant}
                  title={isAll ? t('Annual Spending') : `${t(MONTHS.find(m => m.value === selectedMonth)?.label)} ${t('Spending')}`}
                  amount={
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, flexWrap: 'wrap' }}>
                        <Typography
                          component="span"
                          sx={{
                            fontWeight: 800,
                            fontFamily: '"Manrope", sans-serif',
                            fontSize: { xs: '1.6rem', sm: '2rem' },
                            lineHeight: 1,
                            color: 'inherit'
                          }}
                        >
                          {formatCurrency(periodActualSpent, currency)}
                        </Typography>
                        <Typography
                          component="span"
                          sx={{ fontSize: '0.9rem', opacity: 0.7, fontWeight: 600 }}
                        >
                          {currency}
                        </Typography>
                        {convertedSpent && <ConvertedBadge text={convertedSpent} />}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                        <Typography
                          variant="caption"
                          sx={{ opacity: 0.85, fontWeight: 700, color: 'inherit' }}
                        >
                          {formatCurrency(absBudgetDiff, currency)} {currency} {diffLabel}
                        </Typography>
                        {convertedDiff && <ConvertedBadge text={convertedDiff} />}
                      </Box>
                    </Box>
                  }
                  icon={<TrendingDownIcon />}
                  progress={spentPercent}
                  progressLabel={`${spentPercent.toFixed(1)}%`}
                  sx={{ height: '100%' }}
                />
              );
            })()}
          </Box>

          <Box sx={{ minWidth: 0, height: '100%' }}>
            {(() => {
              const currency = budget?.currency || preferredCurrency;
              const income = parseFloat(budget?.income_amount || 0);
              let absSavings = 0;
              let rate = 0;

              if (selectedMonth === 'ALL') {
                absSavings = Math.max(0, income - (summary.total_budgeted || 0));
                rate = income > 0 ? (absSavings / income) * 100 : 0;
              } else {
                const monthlyIncome = income / 12;
                const monthTotal = summary.monthly?.find(m => m.month === selectedMonth)?.total || 0;
                absSavings = Math.max(0, monthlyIncome - monthTotal);
                rate = monthlyIncome > 0 ? (absSavings / monthlyIncome) * 100 : 0;
              }

              const absConverted = buildConverted(absSavings, currency, preferredCurrency, rates, formatCurrency);

              return (
                <SummaryCard
                  variant="light"
                  title={t("Savings Rate")}
                  amount={`${rate.toFixed(1)}%`}
                  icon={<TrendingUpIcon />}
                  subtext={
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 0.5 }}>
                      <Box component="span" sx={{ fontWeight: 800, color: 'success.main' }}>
                        {formatCurrency(absSavings, currency)} {currency}
                      </Box>
                      {absConverted && <ConvertedBadge text={absConverted} />}
                      <Box component="span" sx={{ opacity: 0.85, fontWeight: 600, color: 'text.secondary' }}>
                        {selectedMonth === 'ALL' ? t('annual savings') : `${t(MONTHS.find(m => m.value === selectedMonth)?.label)} ${t('savings')}`}
                      </Box>
                    </Box>
                  }
                  subtextColor="success.main"
                  sx={{ height: '100%' }}
                />
              );
            })()}
          </Box>

          <Box sx={{ minWidth: 0, height: '100%' }}>
            <Paper elevation={0} sx={{ p: 2.5, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <BudgetProgressBar
                spent={
                  selectedMonth === 'ALL'
                    ? summary.total_budgeted
                    : (summary.monthly?.find(m => m.month === selectedMonth)?.total || 0)
                }
                income={
                  selectedMonth === 'ALL'
                    ? (budget?.income_amount || 0)
                    : ((budget?.income_amount || 0) / 12)
                }
                currency={budget?.currency || preferredCurrency}
                preferredCurrency={preferredCurrency}
                formatCurrency={formatCurrency}
                rates={rates}
                onProgressClick={() => handleOpenTxModal(selectedMonth === 'ALL' ? t('Overall Budget') : `${t(MONTHS.find(m => m.value === selectedMonth)?.label)} ${t('Budget')}`, null)}
                isMonthly={selectedMonth !== 'ALL'}
                monthLabel={selectedMonth !== 'ALL' ? MONTHS.find(m => m.value === selectedMonth)?.label : ''}
              />

              {mode === 'edit' && lastYearIncome > 0 && budget?.income_amount === 0 && (
                <Alert
                  severity="info"
                  sx={{ mt: 2 }}
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      onClick={() => handleUpdateBudget({ income_amount: lastYearIncome })}
                    >
                      {t('Apply')}
                    </Button>
                  }
                >
                  {t("Last year's actual income was")} <strong>{formatCurrency(lastYearIncome, budget?.currency || preferredCurrency)}</strong>. {t('Would you like to use this for')} {selectedYear}?
                </Alert>
              )}
              {mode === 'edit' && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    px: 2,
                    borderRadius: 2,
                    bgcolor: 'rgba(10, 42, 33, 0.04)',
                    border: '1px solid rgba(10, 42, 33, 0.08)',
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: { xs: 'flex-start', sm: 'center' },
                    justifyContent: 'space-between',
                    gap: 1.5
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccountBalanceIcon sx={{ fontSize: 18, color: arborGreen }} />
                    <Typography variant="body2" sx={{ fontWeight: 700, color: arborGreen, fontSize: '0.85rem' }}>
                      {t('Update Annual Income')}
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', width: { xs: '100%', sm: 'auto' } }}>
                    <TextField
                      id="annual-income-input"
                      variant="standard"
                      size="small"
                      placeholder="0.00"
                      slotProps={{
                        htmlInput: {
                          inputMode: 'decimal',
                          'aria-label': t('Update Annual Income')
                        },
                        input: {
                          disableUnderline: true,
                          startAdornment: (
                            <InputAdornment position="start" sx={{ mr: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 800, color: arborGreen }}>
                                {getCurrencySymbol(budget?.currency)}
                              </Typography>
                            </InputAdornment>
                          ),
                        }
                      }}
                      value={formatWithCommas(incomeInput)}
                      onFocus={(e) => handleNumericFocus(e, (ev) => {
                        const raw = stripCommas(ev.target.value);
                        if (raw) setIncomeInput(raw);
                      })}
                      onChange={(e) => {
                        const raw = stripCommas(e.target.value);
                        if (raw === '' || !isNaN(raw)) {
                          setIncomeInput(raw);
                        }
                      }}
                      onBlur={handleIncomeBlur}
                      disabled={isIncomeSaving}
                      sx={{
                        bgcolor: '#fff',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 1.5,
                        border: '1px solid',
                        borderColor: alpha(arborGreen, 0.2),
                        width: { xs: '100%', sm: 140 },
                        transition: 'all 0.2s ease',
                        '&:focus-within': {
                          borderColor: arborGreen,
                          boxShadow: `0 0 0 2px ${alpha(arborGreen, 0.15)}`,
                        },
                        '& input': {
                          fontWeight: 800,
                          fontSize: '0.95rem',
                          color: arborGreen,
                          p: 0,
                          textAlign: { xs: 'left', sm: 'right' }
                        }
                      }}
                    />
                    {isIncomeSaving && (
                      <CircularProgress size={16} sx={{ color: arborGreen }} />
                    )}
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        </Box>
      )}

      {/* ── AI Financial Insights ── */}
      <Box sx={{ mb: 5 }}>
        <AdvisorInsightsSection
          ledgerId={ledgerId}
          scope="BUDGET"
          year={selectedYear}
          title={`${t('AI Budget Insights')} (${selectedYear})`}
          subtitle={t("AI analysis on spending patterns, over/under budget trends, and potential savings opportunities.")}
        />
      </Box>

      {/* ── Budget Tree ── */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Typography variant="h6" sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccountBalanceIcon color="primary" /> {t('Budget Breakdown')}
            </Typography>
            {isRefreshing && <CircularProgress size={16} sx={{ color: 'primary.main' }} />}
          </Box>
          {mode === 'edit' && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setCategorySearch('');
                setCategoryDialogOpen(true);
              }}
              sx={{ fontWeight: 700 }}
            >
              {t('Add Category')}
            </Button>
          )}
        </Box>

        {isRefreshing && (
          <LinearProgress sx={{ height: 3, borderRadius: 1.5, mb: 2 }} />
        )}

        {budget?.items.length === 0 ? (
          <Paper elevation={0} sx={{ p: 8, textAlign: 'center', border: '2px dashed', borderColor: 'divider', bgcolor: 'transparent', borderRadius: 1 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>{t('No budget items found for')} {selectedYear}</Typography>
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
              {priorYearWithData && (
                <Button
                  variant="contained"
                  startIcon={<ContentCopyIcon />}
                  onClick={() => {
                    setCloneOverwrite(false);
                    setCloneDialogOpen(true);
                  }}
                  sx={{ fontWeight: 700 }}
                >
                  {t('Clone from')} {priorYearWithData}
                </Button>
              )}
              {budget?.has_prior_data && (
                <Button
                  variant="outlined"
                  startIcon={<RestoreIcon />}
                  onClick={handleSeedTemplate}
                  sx={{ fontWeight: 700 }}
                >
                  {t('Initialize from History')}
                </Button>
              )}
            </Stack>
          </Paper>
        ) : (
          <Stack spacing={2}>
            {sortedBudgetItems.map((catGroup) => {
              const budgetCurrency = budget?.currency || preferredCurrency;
              const catCurrency = budgetCurrency;

              const getItemContribution = (item) => {
                if (item.sub_items && item.sub_items.length > 0) {
                  return item.sub_items.reduce((sum, child) => sum + getItemContribution(child), 0);
                }
                if (selectedMonth !== 'ALL' && item.frequency_type === BudgetFrequency.SPECIFIC_MONTHS && !item.months?.includes(selectedMonth)) {
                  return 0;
                }
                const amt = parseFloat(item.amount || 0);
                let convertedAmt = amt;
                if (item.currency && item.currency !== budgetCurrency && budgetRates?.[item.currency]) {
                  convertedAmt = amt * budgetRates[item.currency];
                }
                if (selectedMonth === 'ALL') {
                  return item.frequency_type === BudgetFrequency.MONTHLY ? convertedAmt * 12 : convertedAmt * (item.months?.length || 0);
                } else {
                  return convertedAmt;
                }
              };

              const countLeafItems = (node) => {
                if (!node) return 0;
                if (node.sub_items && node.sub_items.length > 0) {
                  return node.sub_items.reduce((sum, child) => sum + countLeafItems(child), 0);
                }
                const isTopCategory = !node.parent_budget_item_id && node.category_id && !categories.find(c => c.id === node.category_id)?.parent_id;
                const isSubCategory = node.category_id && categories.find(c => c.id === node.category_id)?.parent_id;
                if (isTopCategory || isSubCategory) {
                  return 0;
                }
                return 1;
              };

              const catTotal = getItemContribution(catGroup);
              const catActualObj = actualsByCategoryMap.get(catGroup.category_id);
              const catActual = catActualObj ? (selectedMonth === 'ALL' ? catActualObj.ALL : (catActualObj[selectedMonth] || 0)) : 0;
              const actualPercent = catTotal > 0 ? Math.min((catActual / catTotal) * 100, 100) : 0;
              const isOverCat = catActual > catTotal;

              const subItemsList = [...(catGroup.sub_items || [])].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
              const totalItemsCount = countLeafItems(catGroup);

              return (
                <Paper elevation={0} key={catGroup.id || catGroup.category_id} sx={{ overflow: 'hidden', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <Box
                    onClick={() => toggleExpand(catGroup.category_id || catGroup.id)}
                    sx={{
                      p: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 3 },
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      justifyContent: 'space-between',
                      alignItems: { xs: 'stretch', sm: 'center' },
                      gap: { xs: 1.5, sm: 2 },
                      cursor: 'pointer', bgcolor: 'action.hover',
                      '&:hover': { bgcolor: 'action.selected' }
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {expandedCats[catGroup.category_id || catGroup.id] !== false ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                          {t(catGroup.name)}
                        </Typography>
                        <Chip label={`${totalItemsCount} ${totalItemsCount === 1 ? t('item') : t('items')}`} size="small" variant="outlined" sx={{ fontWeight: 600, height: 20, fontSize: '0.65rem' }} />
                      </Box>
                      {mode === 'edit' && (
                        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                          <Tooltip title={t("Remove Category")}>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleDeleteItem(catGroup.id); }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: { xs: 'space-between', sm: 'flex-end' }, alignItems: 'center', gap: { xs: 1.5, sm: 3 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 3 }, width: { xs: '100%', sm: 'auto' }, justifyContent: { xs: 'space-between', sm: 'flex-end' } }}>
                        {/* Actuals Progress Bar */}
                        <Tooltip title={`${t('Click to view transactions for')} ${t(catGroup.name)}`}>
                          <Box
                            title={`${t('Click to view transactions for')} ${t(catGroup.name)}`}
                            data-testid={`budget-progress-${catGroup.name}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              const allCatIds = [catGroup.category_id, ...(catGroup.sub_items || []).map(child => child.category_id)].filter(Boolean);
                              handleOpenTxModal(catGroup.name, allCatIds);
                            }}
                            sx={{
                              width: { xs: 100, sm: 120 },
                              textAlign: 'right',
                              cursor: 'pointer',
                              p: 0.5,
                              borderRadius: 1,
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                bgcolor: 'action.hover',
                                transform: 'scale(1.03)',
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: isOverCat ? 'error.main' : 'text.secondary' }}>
                                {formatCurrency(catActual, catCurrency)}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                {Math.round(actualPercent)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={actualPercent}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: 'divider',
                                '& .MuiLinearProgress-bar': {
                                  bgcolor: isOverCat ? '#c62828' : 'primary.main',
                                  boxShadow: isOverCat ? '0 0 8px rgba(198, 40, 40, 0.4)' : 'none',
                                }
                              }}
                            />
                          </Box>
                        </Tooltip>

                        <Box sx={{ textAlign: 'right' }}>
                          {(() => {
                            const catConverted = buildConverted(catTotal, catCurrency, preferredCurrency, rates, formatCurrency);
                            return (
                              <>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                                    {formatCurrency(catTotal, catCurrency)}
                                  </Typography>
                                  <Typography component="span" variant="caption" sx={{ opacity: 0.6, fontWeight: 600 }}>
                                    {catCurrency}
                                  </Typography>
                                  <ConvertedBadge text={catConverted} />
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                  {selectedMonth === 'ALL' ? t('ANNUAL TOTAL') : t('MONTHLY BUDGET')}
                                </Typography>
                              </>
                            );
                          })()}
                        </Box>
                      </Box>
                      {mode === 'edit' && (
                        <Box sx={{ display: { xs: 'none', sm: 'flex' }, gap: 1 }}>
                          <Tooltip title={t("Remove Category")}>
                            <IconButton
                              size="small"
                              onClick={(e) => { e.stopPropagation(); handleDeleteItem(catGroup.id); }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </Box>

                  <Collapse in={expandedCats[catGroup.category_id || catGroup.id] !== false}>
                    <Divider />
                    <Box sx={{ p: 0 }}>
                      {(!subItemsList || subItemsList.length === 0) && (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography variant="body2" color="text.disabled">
                            {t('No budget items in this category yet.')}
                          </Typography>
                        </Box>
                      )}
                      {subItemsList.map((item, idx) => {
                        const isSubcategory = Boolean(item.sub_items && item.sub_items.length > 0) ||
                          Boolean(categories.find(c => c.id === item.category_id)?.parent_id);

                        if (isSubcategory) {
                          const subTotal = getItemContribution(item);
                          const subActualObj = actualsByCategoryMap.get(item.category_id);
                          const subActual = subActualObj ? (selectedMonth === 'ALL' ? subActualObj.ALL : (subActualObj[selectedMonth] || 0)) : 0;
                          const subPercent = subTotal > 0 ? Math.min((subActual / subTotal) * 100, 100) : 0;
                          const isOverSub = subActual > subTotal;
                          const subChildren = item.sub_items || [];

                          return (
                            <Box key={item.id} sx={{ p: { xs: 1.5, sm: 2 }, px: { xs: 1.5, sm: 3 }, bgcolor: 'rgba(0,0,0,0.01)', borderBottom: '1px solid', borderColor: 'divider' }}>
                              {/* Subcategory Header */}
                              <Box sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                justifyContent: 'space-between',
                                alignItems: { xs: 'stretch', sm: 'center' },
                                gap: 1,
                                mb: subChildren.length > 0 ? 1.5 : 0.5
                              }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                                    📁 {t(item.name)}
                                  </Typography>
                                  <Chip label={t("Subcategory")} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 600 }} />
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: { xs: 'space-between', sm: 'flex-end' }, alignItems: 'center', gap: 2 }}>
                                  <Tooltip title={`${t('Click to view transactions for')} ${t(item.name)}`}>
                                    <Box
                                      title={`${t('Click to view transactions for')} ${t(item.name)}`}
                                      data-testid={`budget-progress-${item.name}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleOpenTxModal(item.name, [item.category_id].filter(Boolean));
                                      }}
                                      sx={{
                                        width: { xs: 100, sm: 120 },
                                        textAlign: 'right',
                                        cursor: 'pointer',
                                        p: 0.5,
                                        borderRadius: 1,
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                          bgcolor: 'action.hover',
                                          transform: 'scale(1.03)',
                                        }
                                      }}
                                    >
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: isOverSub ? 'error.main' : 'text.secondary' }}>
                                          {formatCurrency(subActual, catCurrency)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                          {Math.round(subPercent)}%
                                        </Typography>
                                      </Box>
                                      <LinearProgress
                                        variant="determinate"
                                        value={subPercent}
                                        sx={{
                                          height: 6,
                                          borderRadius: 3,
                                          bgcolor: 'divider',
                                          '& .MuiLinearProgress-bar': {
                                            bgcolor: isOverSub ? '#c62828' : 'primary.main',
                                            boxShadow: isOverSub ? '0 0 8px rgba(198, 40, 40, 0.4)' : 'none',
                                          }
                                        }}
                                      />
                                    </Box>
                                  </Tooltip>
                                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                    {formatCurrency(subTotal, catCurrency)}
                                  </Typography>
                                  {mode === 'edit' && (
                                    <IconButton size="small" color="error" onClick={() => handleDeleteItem(item.id)} aria-label={t("Delete item")}>
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  )}
                                </Box>
                              </Box>

                              {/* Nested Subcategory Line Items */}
                              <Box sx={{ pl: { xs: 1, sm: 2 }, borderLeft: '2px dashed', borderColor: 'divider', ml: { xs: 0.5, sm: 1 } }}>
                                {subChildren.length === 0 ? (
                                  <Box sx={{ py: 1.5, px: 1 }}>
                                    <Typography variant="caption" color="text.disabled">
                                      {t('No budget items in this subcategory yet.')}
                                    </Typography>
                                  </Box>
                                ) : (
                                  subChildren.map((subLeaf, sIdx) => {
                                    const leafAmt = parseFloat(subLeaf.amount || 0);
                                    const viewLeafAmt = selectedMonth === 'ALL'
                                      ? (subLeaf.frequency_type === 'MONTHLY' ? leafAmt * 12 : leafAmt * (subLeaf.months?.length || 0))
                                      : leafAmt;
                                    const leafConverted = buildConverted(viewLeafAmt, subLeaf.currency, preferredCurrency, rates, formatCurrency);

                                    return (
                                      <React.Fragment key={subLeaf.id}>
                                        <Box sx={{
                                          py: 1,
                                          px: { xs: 1, sm: 2 },
                                          display: 'flex',
                                          flexDirection: { xs: 'column', sm: 'row' },
                                          justifyContent: 'space-between',
                                          alignItems: { xs: 'stretch', sm: 'center' },
                                          gap: 1
                                        }}>
                                          <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                              {t(subLeaf.name)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                              {subLeaf.frequency_type === 'MONTHLY'
                                                ? `${formatCurrency(leafAmt, subLeaf.currency)} / ${t('month')}`
                                                : `${formatCurrency(leafAmt, subLeaf.currency)} / ${t('month')} ${formatSpecificMonthsText(subLeaf.months, t)}`}
                                            </Typography>
                                          </Box>
                                          <Box sx={{ display: 'flex', justifyContent: { xs: 'space-between', sm: 'flex-end' }, alignItems: 'center', gap: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                                              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {formatCurrency(viewLeafAmt, subLeaf.currency)}
                                              </Typography>
                                              <ConvertedBadge text={leafConverted} />
                                            </Box>
                                            {mode === 'edit' && userRole !== 'viewer' && (
                                              <Box sx={{ display: 'flex' }}>
                                                <IconButton size="small" onClick={() => handleOpenEdit(subLeaf)} aria-label={t("Edit item")}>
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => handleDeleteItem(subLeaf.id)} aria-label={t("Delete item")}>
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              </Box>
                                            )}
                                          </Box>
                                        </Box>
                                        {sIdx < subChildren.length - 1 && <Divider sx={{ my: 0.5 }} />}
                                      </React.Fragment>
                                    );
                                  })
                                )}
                                {mode === 'edit' && (
                                  <Button
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={() => handleOpenAdd(item)}
                                    sx={{ fontWeight: 700, mt: 1, fontSize: '0.75rem' }}
                                  >
                                    {t('Add Item to')} {t(item.name)}
                                  </Button>
                                )}
                              </Box>
                            </Box>
                          );
                        }

                        // Regular Line Item directly under category
                        const amt = parseFloat(item.amount || 0);
                        const viewAmt = selectedMonth === 'ALL'
                          ? (item.frequency_type === 'MONTHLY' ? amt * 12 : amt * (item.months?.length || 0))
                          : amt;
                        const lineConverted = buildConverted(viewAmt, item.currency, preferredCurrency, rates, formatCurrency);

                        return (
                          <React.Fragment key={item.id}>
                            <Box sx={{
                              p: { xs: 1.5, sm: 2 },
                              px: { xs: 1.5, sm: 4 },
                              display: 'flex',
                              flexDirection: { xs: 'column', sm: 'row' },
                              justifyContent: 'space-between',
                              alignItems: { xs: 'stretch', sm: 'center' },
                              gap: 1,
                              '&:hover': { bgcolor: 'action.hover' }
                            }}>
                              <Box
                                onClick={item.category_id ? () => handleOpenTxModal(item.name, [item.category_id]) : undefined}
                                sx={{
                                  cursor: item.category_id ? 'pointer' : 'default',
                                  transition: 'opacity 0.15s ease',
                                  '&:hover': item.category_id ? { opacity: 0.8 } : {}
                                }}
                              >
                                <Tooltip title={item.category_id ? `${t('Click to view transactions for')} ${t(item.name)}` : ''} disableHoverListener={!item.category_id}>
                                  <Box>
                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                      {t(item.name)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap' }}>
                                      <Typography variant="caption" color="text.secondary">
                                        {item.frequency_type === 'MONTHLY'
                                          ? `${formatCurrency(parseFloat(item.amount), item.currency)} / ${t('month')}`
                                          : `${formatCurrency(parseFloat(item.amount), item.currency)} / ${t('month')} ${formatSpecificMonthsText(item.months, t)}`}
                                      </Typography>
                                      <Typography component="span" variant="caption" sx={{ opacity: 0.55, fontWeight: 600, color: 'text.secondary' }}>
                                        {item.currency}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Tooltip>
                              </Box>

                              <Box sx={{ display: 'flex', justifyContent: { xs: 'space-between', sm: 'flex-end' }, alignItems: 'center', gap: 2 }}>
                                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                  <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                    {formatCurrency(viewAmt, item.currency)}
                                  </Typography>
                                  <Typography component="span" variant="caption" sx={{ opacity: 0.6, fontWeight: 600 }}>
                                    {item.currency}
                                  </Typography>
                                  <ConvertedBadge text={lineConverted} />
                                </Box>
                                {mode === 'edit' && userRole !== 'viewer' && (
                                  <Box sx={{ display: 'flex' }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleOpenEdit(item)}
                                      aria-label={t("Edit item")}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteItem(item.id)}
                                      aria-label={t("Delete item")}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Box>
                                )}
                              </Box>
                            </Box>
                            {idx < subItemsList.length - 1 && <Divider sx={{ mx: 2 }} />}
                          </React.Fragment>
                        );
                      })}
                      {mode === 'edit' && (
                        <Box sx={{ p: 2, px: 4, bgcolor: 'background.paper' }}>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenAdd(catGroup)}
                            sx={{ fontWeight: 700 }}
                          >
                            {t('Add to')} {t(catGroup.name)}
                          </Button>
                        </Box>
                      )}
                    </Box>
                  </Collapse>
                </Paper>
              );
            })}
          </Stack>
        )}

        {/* ── Uncategorized Spendings Alert ── */}
        {uncategorizedCount > 0 && (
          <Paper
            elevation={0}
            data-testid="uncategorized-spending-alert"
            sx={{
              mt: 4,
              p: { xs: 2, sm: 2.5 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: alpha('#E5A00D', 0.35),
              bgcolor: alpha('#FFF8E7', 0.8),
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: 'space-between',
              gap: 2,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
              <InfoOutlinedIcon sx={{ color: '#C07E00', fontSize: 24, mt: 0.25, flexShrink: 0 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#663C00', mb: 0.25 }}>
                  {t('Uncategorized Spending')}
                </Typography>
                <Typography variant="body2" sx={{ color: '#805000', fontWeight: 500 }}>
                  {uncategorizedCount === 1
                    ? t('You have {{count}} uncategorized spending transaction totaling {{amount}} in {{period}}.', {
                        count: uncategorizedCount,
                        amount: `${formatCurrency(uncategorizedAmount, budget?.currency || preferredCurrency)} ${budget?.currency || preferredCurrency}`,
                        period: selectedPeriodLabel,
                      })
                    : t('You have {{count}} uncategorized spending transactions totaling {{amount}} in {{period}}.', {
                        count: uncategorizedCount,
                        amount: `${formatCurrency(uncategorizedAmount, budget?.currency || preferredCurrency)} ${budget?.currency || preferredCurrency}`,
                        period: selectedPeriodLabel,
                      })}
                </Typography>
              </Box>
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ alignSelf: { xs: 'stretch', sm: 'center' }, justifyContent: { xs: 'flex-end', sm: 'flex-end' }, flexShrink: 0 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleOpenTxModal(t('Uncategorized'), [], true)}
                sx={{
                  fontWeight: 700,
                  borderColor: '#C07E00',
                  color: '#663C00',
                  textTransform: 'none',
                  borderRadius: 1.5,
                  '&:hover': {
                    borderColor: '#805000',
                    bgcolor: alpha('#E5A00D', 0.08),
                  }
                }}
              >
                {t('View Transactions')}
              </Button>
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate(`/ledger/${ledgerId}`)}
                sx={{
                  fontWeight: 700,
                  bgcolor: '#805000',
                  color: '#fff',
                  textTransform: 'none',
                  borderRadius: 1.5,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: '#663C00',
                    boxShadow: 'none',
                  }
                }}
              >
                {t('Go to Transactions')}
              </Button>
            </Stack>
          </Paper>
        )}
      </Box>

      {/* ── Item Dialog ── */}
      <FormDialog
        open={dialogOpen}
        onClose={isSaving ? undefined : () => setDialogOpen(false)}
        onSubmit={handleSaveItem}
        title={currentItem ? t('Edit Budget Item') : (parentItemContext?.name ? `${t('Add Item to')} ${t(parentItemContext.name)}` : t('New Budget Item'))}
        saving={isSaving}
        maxWidth="xs"
      >
        <Stack spacing={4}>
          <FormSection>
            <FormField label={t("Category")}>
              <Typography variant="h6" sx={{ fontWeight: 800, color: arborGreen }}>
                {parentItemContext ? t(parentItemContext.name) : t(getCategoryName(formData.category_id))}
              </Typography>
              {parentItemContext && formData.category_id && parentItemContext.name !== getCategoryName(formData.category_id) && (
                <Typography variant="caption" color="text.secondary">
                  {t("Parent")}: {t(getCategoryName(formData.category_id))}
                </Typography>
              )}
            </FormField>
          </FormSection>

          <FormSection>
            <FormField label={t("Description")} required>
              <TextField
                autoFocus
                fullWidth
                variant="filled"
                placeholder={t("e.g., Grocery Allowance, Car Insurance")}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                sx={filledInputSx}
                slotProps={{ input: { disableUnderline: true } }}
              />
            </FormField>
          </FormSection>

          <FormSection title={t("Frequency & Amount")}>
            <Stack spacing={3}>
              <FormField label={t("Frequency")}>
                <ToggleButtonGroup
                  fullWidth
                  exclusive
                  value={formData.frequency}
                  onChange={(e, val) => val && setFormData({ ...formData, frequency: val })}
                >
                  <ToggleButton value={BudgetFrequency.MONTHLY}>{t("Monthly")}</ToggleButton>
                  <ToggleButton value={BudgetFrequency.SPECIFIC_MONTHS}>{t("Specific Months")}</ToggleButton>
                </ToggleButtonGroup>
              </FormField>

              <Stack direction="row" spacing={2} alignItems="flex-end">
                <FormField label={t("Currency")} sx={{ width: { xs: 110, sm: 120 } }}>
                  <Autocomplete
                    options={Object.values(Currency)}
                    value={formData.currency || Currency.USD}
                    onChange={(e, newValue) => {
                      if (newValue) setFormData(prev => ({ ...prev, currency: newValue }));
                    }}
                    disableClearable
                    autoHighlight
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="filled"
                        fullWidth
                        sx={filledInputSx}
                        InputProps={{ ...params.InputProps, disableUnderline: true }}
                      />
                    )}
                  />
                </FormField>
                <FormField label={t("Amount")} required sx={{ flex: 1 }}>
                  <TextField
                    variant="filled"
                    fullWidth
                    slotProps={{
                      htmlInput: { inputMode: 'decimal' },
                      input: {
                        disableUnderline: true,
                        startAdornment: (
                          <InputAdornment position="start" sx={{ opacity: 0.5, fontWeight: 700 }}>
                            {getCurrencySymbol(formData.currency)}
                          </InputAdornment>
                        ),
                      }
                    }}
                    value={formatWithCommas(formData.amount)}
                    onFocus={(e) => handleNumericFocus(e, (ev) => {
                      const raw = stripCommas(ev.target.value);
                      if (raw) setFormData(prev => ({ ...prev, amount: raw }));
                    })}
                    onChange={(e) => {
                      const raw = stripCommas(e.target.value);
                      if (raw === '' || !isNaN(raw)) {
                        setFormData({ ...formData, amount: raw });
                      }
                    }}
                    sx={{ ...filledInputSx, ...NO_SPIN_SX }}
                  />
                </FormField>
              </Stack>

              {formData.frequency === BudgetFrequency.SPECIFIC_MONTHS && (
                <FormField label={t("Select Months")}>
                  <Box sx={{ width: '100%', pt: 0.5 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        {formData.months?.length === 12
                          ? t('All 12 Months Selected')
                          : `${formData.months?.length || 0} / 12 ${t('Months Selected')}`}
                      </Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setFormData({ ...formData, months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] })}
                          sx={{ fontSize: '0.75rem', py: 0, px: 0.75, minWidth: 'auto', textTransform: 'none', fontWeight: 700, color: arborGreen }}
                        >
                          {t('Select All')}
                        </Button>
                        <Button
                          size="small"
                          variant="text"
                          onClick={() => setFormData({ ...formData, months: [] })}
                          sx={{ fontSize: '0.75rem', py: 0, px: 0.75, minWidth: 'auto', textTransform: 'none', fontWeight: 700, color: 'text.secondary' }}
                        >
                          {t('Clear')}
                        </Button>
                      </Stack>
                    </Box>

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 1,
                      }}
                    >
                      {MONTHS.map((m) => {
                        const isSelected = formData.months?.includes(m.value);
                        return (
                          <Tooltip key={m.value} title={t(m.label)} arrow placement="top">
                            <Button
                              variant={isSelected ? 'contained' : 'outlined'}
                              size="small"
                              onClick={() => {
                                const current = formData.months || [];
                                const updated = current.includes(m.value)
                                  ? current.filter((v) => v !== m.value)
                                  : [...current, m.value].sort((a, b) => a - b);
                                setFormData({ ...formData, months: updated });
                              }}
                              sx={{
                                py: 0.85,
                                px: 0.5,
                                minWidth: 0,
                                borderRadius: '10px',
                                fontWeight: isSelected ? 700 : 500,
                                fontSize: '0.85rem',
                                textTransform: 'none',
                                bgcolor: isSelected ? arborGreen : 'rgba(0, 109, 67, 0.04)',
                                borderColor: isSelected ? arborGreen : 'rgba(0, 109, 67, 0.15)',
                                color: isSelected ? '#fff' : 'text.primary',
                                boxShadow: isSelected ? '0 2px 4px rgba(0, 109, 67, 0.25)' : 'none',
                                '&:hover': {
                                  bgcolor: isSelected ? '#005433' : 'rgba(0, 109, 67, 0.1)',
                                  borderColor: isSelected ? '#005433' : 'rgba(0, 109, 67, 0.3)',
                                },
                              }}
                            >
                              {t(m.short)}
                            </Button>
                          </Tooltip>
                        );
                      })}
                    </Box>
                  </Box>
                </FormField>
              )}

              {formData.amount && !isNaN(parseFloat(formData.amount)) && (
                <Box sx={{
                  p: 2,
                  bgcolor: 'rgba(45, 106, 79, 0.05)',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  border: '1px solid rgba(45, 106, 79, 0.1)'
                }}>
                  <TrendingDownIcon sx={{ color: '#2D6A4F', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#2D6A4F' }}>
                    {t("Annual Impact:")} {formatCurrency(parseFloat(formData.amount) * (formData.frequency === BudgetFrequency.MONTHLY ? 12 : (formData.months?.length || 0)), formData.currency)}
                  </Typography>
                </Box>
              )}
            </Stack>
          </FormSection>
        </Stack>
      </FormDialog>

      {/* ── Category Dialog ── */}
      <Dialog
        open={categoryDialogOpen}
        onClose={() => setCategoryDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            bgcolor: creamBg,
            borderRadius: { xs: 2, sm: '32px' },
            backgroundImage: 'none',
            overflow: 'hidden'
          }
        }}
      >
        <Box sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Box sx={{ mb: { xs: 2, sm: 4 } }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1, color: arborGreen, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              {t("Select Category to Add")}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(10, 42, 33, 0.6)', fontWeight: 500 }}>
              {t("Choose a category from your ledger to add to your")} {selectedYear} {t("budget")}.
            </Typography>

            <TextField
              autoFocus
              fullWidth
              placeholder={t("Search categories...")}
              variant="filled"
              size="small"
              value={categorySearch}
              onChange={(e) => setCategorySearch(e.target.value)}
              sx={{ ...filledInputSx, mt: { xs: 2, sm: 3 } }}
              InputProps={{
                disableUnderline: true,
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ opacity: 0.4 }} /></InputAdornment>
              }}
            />
          </Box>

          <Box sx={{ minHeight: 300, maxHeight: '60vh', overflowY: 'auto', py: 1 }}>
            <Grid container spacing={2}>
              {availableCategories.map(cat => {
                const catNameLower = cat.name.toLowerCase();
                const matched = ICON_RULES.find(rule =>
                  rule.keywords.some(k => catNameLower.includes(k.toLowerCase()))
                );
                const icon = matched ? matched.icon : <CategoryIcon />;
                const parentCategory = cat.parent_id ? categories.find(c => c.id === cat.parent_id) : null;

                return (
                  <Grid item xs={12} sm={6} md={4} key={cat.id}>
                    <Button
                      fullWidth
                      disabled={addingCategoryId !== null}
                      onClick={() => handleAddCategory(cat.id)}
                      sx={{
                        display: 'block',
                        p: 0,
                        textAlign: 'left',
                        overflow: 'hidden',
                        textTransform: 'none',
                        borderRadius: 2,
                        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        bgcolor: '#fff',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                          borderColor: 'primary.main',
                          transform: 'translateY(-4px)',
                          boxShadow: (theme) => `0 12px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
                          bgcolor: '#fff'
                        },
                        '&.Mui-disabled': {
                          bgcolor: addingCategoryId === cat.id ? 'rgba(10, 42, 33, 0.03)' : '#fff',
                          opacity: addingCategoryId === cat.id ? 1 : 0.6,
                        }
                      }}
                    >
                      <Box sx={{ p: { xs: 2, sm: 3 } }}>
                        <Box sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 48, height: 48,
                          borderRadius: 2,
                          bgcolor: 'rgba(10, 42, 33, 0.05)',
                          color: arborGreen,
                          mb: 2,
                        }}>
                          {addingCategoryId === cat.id ? (
                            <CircularProgress size={24} sx={{ color: arborGreen }} />
                          ) : (
                            icon
                          )}
                        </Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 0.5, color: arborGreen }}>
                          {t(cat.name)}
                        </Typography>
                        {parentCategory && (
                          <Chip
                            label={`${t("Subcategory of")} ${t(parentCategory.name)}`}
                            size="small"
                            variant="outlined"
                            sx={{ mb: 1, height: 18, fontSize: '0.65rem', fontWeight: 600 }}
                          />
                        )}
                        <Typography variant="caption" sx={{ color: 'rgba(10, 42, 33, 0.5)', display: 'block', minHeight: 32, fontWeight: 500 }}>
                          {addingCategoryId === cat.id ? t('Adding category...') : (cat.description ? t(cat.description) : `${t('Plan your')} ${t(cat.name).toLowerCase()} ${t('spending')}.`)}
                        </Typography>
                      </Box>
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
            {availableCategories.length === 0 && (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography variant="h6" sx={{ color: 'rgba(10, 42, 33, 0.3)', fontWeight: 700 }}>{t("No categories found")}</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={() => {
                setCategoryDialogOpen(false);
                setCategorySearch('');
              }}
              sx={{
                fontWeight: 700,
                px: 4,
                color: arborGreen,
                textTransform: 'none'
              }}
            >
              {t("Close")}
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* ── Clone Dialog ── */}
      <FormDialog
        open={cloneDialogOpen}
        onClose={isSaving ? undefined : () => setCloneDialogOpen(false)}
        onSubmit={handleCloneBudget}
        title={`${t("Clone Budget from")} ${priorYearWithData}`}
        saveLabel={t("Confirm Clone")}
        saving={isSaving}
        maxWidth="xs"
      >
        <Stack spacing={3}>
          <Typography variant="body2" sx={{ color: 'rgba(10, 42, 33, 0.6)', fontWeight: 500, lineHeight: 1.6 }}>
            {t("This will copy all income settings and budget line items from")} <strong>{priorYearWithData}</strong> {t("into your")} <strong>{selectedYear}</strong> {t("budget")}.
          </Typography>

          {budget?.items?.length > 0 && (
            <Alert
              severity="warning"
              sx={{
                borderRadius: 2,
                '& .MuiAlert-message': { fontWeight: 500 }
              }}
            >
              {t("Your")} {selectedYear} {t("budget already has")} {budget.items.length} {t("categories")}. {t("Choose how you want to handle existing data:")}
            </Alert>
          )}

          {budget?.items?.length > 0 && (
            <FormField label={t("Cloning Strategy")}>
              <ToggleButtonGroup
                fullWidth
                exclusive
                value={cloneOverwrite}
                onChange={(e, val) => val !== null && setCloneOverwrite(val)}
                size="small"
                sx={{
                  bgcolor: 'rgba(10, 42, 33, 0.05)',
                  borderRadius: 2,
                  p: 0.5,
                  '& .MuiToggleButton-root': {
                    borderRadius: 1.5,
                    border: 'none',
                    textTransform: 'none',
                    fontWeight: 600,
                    py: 1,
                    color: 'rgba(10, 42, 33, 0.5)',
                    '&.Mui-selected': {
                      bgcolor: '#fff',
                      color: arborGreen,
                      boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                      '&:hover': { bgcolor: '#fff' }
                    }
                  }
                }}
              >
                <ToggleButton value={false}>{t("Append Items")}</ToggleButton>
                <ToggleButton value={true}>{t("Overwrite All")}</ToggleButton>
              </ToggleButtonGroup>
            </FormField>
          )}
        </Stack>
      </FormDialog>
      {/* ── Delete Confirmation Dialog ── */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setItemToDelete(null); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{t("Remove Budget Item")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("Are you sure you want to remove this budget item? This cannot be undone.")}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => { setDeleteConfirmOpen(false); setItemToDelete(null); }}
            sx={{ fontWeight: 600 }}
          >
            {t("Cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            sx={{ fontWeight: 700 }}
          >
            {t("Remove")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Transactions List Modal ── */}
      <BudgetTransactionsModal
        open={txModalState.open}
        onClose={handleCloseTxModal}
        api={api}
        ledgerId={ledgerId}
        categoryName={txModalState.categoryName}
        categoryIds={txModalState.categoryIds}
        isUncategorized={txModalState.isUncategorized}
        year={selectedYear}
        month={selectedMonth}
        formatCurrency={formatCurrency}
        preferredCurrency={preferredCurrency}
        categories={categories}
        onTransactionUpdated={fetchAllData}
      />
    </Container>
  );
}
